import { Component } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as fileSaver from 'file-saver';
import { ApplicationSettingsService } from '../application-settings.service';
import { DirectoryService } from '../repository-manager/directory.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { UsersService } from 'src/app/core/user/services/users.service';
import { ChangePasswordRequest, SetPasswordRequest } from 'src/app/shared/models/auth';

@Component({
  selector: 'app-file-manager',
  templateUrl: './file-manager.component.html',
  styleUrls: ['./file-manager.component.less']
})
export class FileManagerComponent {

  userGuide!: File;
  dsiGuide!: File;
  dataGuide!: File;
  commandGuide!: File;
  alertGuide!: File;
  adminGuide!: File;
  facturationGuide!: File;
  facturationGuideAdmin!: File;
  appsGuide!: File;
  conf!: File;

  debugMode = false;

  // Change own password
  showChangePasswordDialog = false;
  oldPassword = '';
  newPassword = '';
  confirmPassword = '';
  isChangingPassword = false;

  // Set password for another user
  showSetPasswordDialog = false;
  targetUserIdentifier = '';
  adminPassword = '';
  adminConfirmPassword = '';
  isSettingPassword = false;

  constructor(public ds: DirectoryService, private _snackBar: MatSnackBar,
    private as: ApplicationSettingsService,
    private snackbarService: SnackbarService,
    private authService: AuthenticationService,
    private usersService: UsersService
  ) {
    const debugMode = localStorage.getItem('debugMode');
    if (debugMode !== null) {
      this.debugMode = JSON.parse(debugMode);
    }
  }

  importFile(file: string) {
    let f: File;
    switch (file) {
      case "userGuide":
        f = this.userGuide;
        break;
      case "dsiGuide":
        f = this.dsiGuide;
        break;
      case "dataGuide":
        f = this.dataGuide;
        break;
      case "commandGuide":
        f = this.commandGuide;
        break;
      case "alertGuide":
        f = this.alertGuide;
        break;
      case "adminGuide":
        f = this.adminGuide;
        break;
      case "facturationGuide":
        f = this.facturationGuide;
        break;
      case "facturationGuideAdmin":
        f = this.facturationGuideAdmin;
        break;
      case "appsGuide":
        f = this.appsGuide;
        break;
      case "conf":
        f = this.conf;
        break;
      default:
        return;
    }
    if (f != null) {
      if (file == "conf") this.ds.importConf(f).subscribe({
        next: res => {
          this.openSuccessSnackBar("Le fichier de configuration s'est correctement importé.");
        },
        error: err => {
          console.log(err);
          this.openErrorSnackBar("Erreur lors de l'import du fichier.");
        },
        complete: () => {
        }
      })
        ;
      else
        this.ds.importFile(f, file).subscribe({
          next: res => {
            this.openSuccessSnackBar("Le guide s'est correctement importé.");
          },
          error: err => {
            console.log(err);
            this.openErrorSnackBar("Erreur lors de l'import du fichier.");
          },
          complete: () => {
          }
        });
    }

  }

  downloadFile(name: string) {
    this.ds.readFileContents(name + ".conf").subscribe(data => {
      const blob: any = new Blob(["\ufeff", data], { type: 'text;charset=utf-8;' });
      window.URL.createObjectURL(blob);
      fileSaver.saveAs(blob, name + this.as.applicationSettings.CDS_name + " - " + new Date().toLocaleDateString().replace('_', '-') + " " + new Date().toLocaleTimeString().replace('_', '-') + ".conf");
    })
  }

  openErrorSnackBar(message: string) {
    this.snackbarService.openSnackBar(message, 'error-snack-bar');
  }

  openSuccessSnackBar(message: string) {
    this.snackbarService.openSnackBar(message);
  }
  

  setDebugMode(value: boolean) {
    this.debugMode = value;
    localStorage.setItem('debugMode', value.toString());
    // refresh de la page
    window.location.reload();
  }

  // Change own password methods
  openChangePasswordDialog() {
    this.oldPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.showChangePasswordDialog = true;
  }

  closeChangePasswordDialog() {
    this.showChangePasswordDialog = false;
    this.oldPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
  }

  changeOwnPassword() {
    if (!this.validateOwnPassword()) {
      return;
    }

    this.isChangingPassword = true;
    const currentUser = this.authService.getUser();
    
    if (!currentUser?.identifier) {
      this.openErrorSnackBar("Erreur: identifiant utilisateur non trouvé.");
      this.isChangingPassword = false;
      return;
    }

    const changePasswordRequest: ChangePasswordRequest = {
      identifier: currentUser.identifier,
      oldPassword: this.oldPassword,
      newPassword: this.newPassword
    };

    this.usersService.changePassword(changePasswordRequest).subscribe({
      next: response => {
        if (response.success) {
          this.openSuccessSnackBar("Mot de passe modifié avec succès !");
          this.closeChangePasswordDialog();
        } else {
          this.openErrorSnackBar(response.message || "Erreur lors de la modification du mot de passe.");
        }
        this.isChangingPassword = false;
      },
      error: error => {
        console.log(error);
        this.isChangingPassword = false;
        
        let errorMessage = "Erreur lors de la modification du mot de passe.";
        if (error.error && error.error.message) {
          errorMessage = error.error.message;
        } else if (error.status === 400) {
          errorMessage = "Ancien mot de passe incorrect ou nouveau mot de passe invalide.";
        } else if (error.status === 401) {
          errorMessage = "Authentification échouée. Veuillez vérifier votre ancien mot de passe.";
        }
        
        this.openErrorSnackBar(errorMessage);
      }
    });
  }

  validateOwnPassword(): boolean {
    if (!this.oldPassword) {
      this.openErrorSnackBar("Veuillez entrer votre ancien mot de passe.");
      return false;
    }

    if (!this.newPassword) {
      this.openErrorSnackBar("Veuillez entrer un nouveau mot de passe.");
      return false;
    }

    if (this.newPassword.length < 8) {
      this.openErrorSnackBar("Le mot de passe doit contenir au moins 8 caractères.");
      return false;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.openErrorSnackBar("Les mots de passe ne correspondent pas.");
      return false;
    }

    if (this.newPassword === this.oldPassword) {
      this.openErrorSnackBar("Le nouveau mot de passe doit être différent de l'ancien.");
      return false;
    }

    return true;
  }

  // Set password for another user methods
  openSetPasswordDialog() {
    this.targetUserIdentifier = '';
    this.adminPassword = '';
    this.adminConfirmPassword = '';
    this.showSetPasswordDialog = true;
  }

  closeSetPasswordDialog() {
    this.showSetPasswordDialog = false;
    this.targetUserIdentifier = '';
    this.adminPassword = '';
    this.adminConfirmPassword = '';
  }

  setPasswordForUser() {
    if (!this.validateAdminPassword()) {
      return;
    }

    this.isSettingPassword = true;

    const setPasswordRequest: SetPasswordRequest = {
      identifier: this.targetUserIdentifier.toUpperCase(),
      password: this.adminPassword
    };

    this.usersService.setInitialPassword(setPasswordRequest).subscribe({
      next: response => {
        if (response.success) {
          this.openSuccessSnackBar(`Mot de passe défini avec succès pour l'utilisateur ${this.targetUserIdentifier.toUpperCase()}`);
          this.closeSetPasswordDialog();
        } else {
          this.openErrorSnackBar(response.message || "Erreur lors de la définition du mot de passe.");
        }
        this.isSettingPassword = false;
      },
      error: error => {
        console.log(error);
        this.isSettingPassword = false;
        
        let errorMessage = "Erreur lors de la définition du mot de passe.";
        if (error.error && error.error.message) {
          errorMessage = error.error.message;
        } else if (error.status === 400) {
          errorMessage = "Mot de passe invalide (minimum 6 caractères).";
        } else if (error.status === 404) {
          errorMessage = "Utilisateur non trouvé.";
        }
        
        this.openErrorSnackBar(errorMessage);
      }
    });
  }

  validateAdminPassword(): boolean {
    if (!this.targetUserIdentifier || this.targetUserIdentifier.trim() === '') {
      this.openErrorSnackBar("Veuillez entrer l'identifiant de l'utilisateur.");
      return false;
    }

    if (!this.adminPassword) {
      this.openErrorSnackBar("Veuillez entrer un mot de passe.");
      return false;
    }

    if (this.adminPassword.length < 6) {
      this.openErrorSnackBar("Le mot de passe doit contenir au moins 6 caractères.");
      return false;
    }

    if (this.adminPassword !== this.adminConfirmPassword) {
      this.openErrorSnackBar("Les mots de passe ne correspondent pas.");
      return false;
    }

    return true;
  }
}